import React, { useState, useEffect } from 'react';
import { useSucursal } from '@/context/SucursalContext';
import useSucursalesFromContext from '@/hooks/useSucursalesFromContext';
import * as repuestosApiService from '@/services/repuestosApiService';
import * as categoriasApiService from '@/services/categoriasApiService';
import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';
import FilterBar from '@/components/common/FilterBar';
import CustomSelect from '@/components/common/CustomSelect';
import CustomCheckbox from '@/components/common/CustomCheckbox';
import CheckboxFiltersContainer from '@/components/common/CheckboxFiltersContainer';
import RepuestosList from './RepuestosList';
import ResumenInventario from './ResumenInventario';
import RepuestoForm from './RepuestoForm';
import { FaPlus, FaFilter, FaSearch, FaMapMarkerAlt } from 'react-icons/fa';
import toastService from '@/utils/toastService';

/**
 * Componente principal para la gestión de repuestos
 * 
 * @returns {JSX.Element} - Componente de mantenedor de repuestos
 */
const RepuestosMantenedor = () => {
  const { selectedSucursal } = useSucursal();
  const { sucursales: sucursalesFromContext, hasValidData } = useSucursalesFromContext();
  const [repuestos, setRepuestos] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [filteredRepuestos, setFilteredRepuestos] = useState([]);
  const [selectedRepuesto, setSelectedRepuesto] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  
  // Filtros
  const [filters, setFilters] = useState({
    categoria: '',
    stockBajo: false,
    searchTerm: '',
    mostrarInactivos: false
  });
  
  // Estado para detectar si estamos en móvil
  const [isMobile, setIsMobile] = useState(false);
  
  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Comprobar inicialmente
    checkMobile();
    
    // Actualizar cuando cambie el tamaño de la ventana
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Categorías disponibles
  // Cargar categorías de repuestos desde la API
  const fetchCategorias = async () => {
    try {
      const categoriasData = await categoriasApiService.getCategoriasByTipo('categoria_repuesto');
      setCategorias(categoriasData);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
      toastService.error('Error al cargar categorías');
    }
  };

  // Cargar categorías al inicializar
  useEffect(() => {
    fetchCategorias();
  }, []);

  // Cargar repuestos y sucursales al iniciar y cuando cambia la sucursal seleccionada
  useEffect(() => {
    fetchData();
  }, [selectedSucursal]);

  // Aplicar filtros cuando cambian
  useEffect(() => {
    applyFilters();
  }, [repuestos, filters]);

  // Función para cargar datos desde la API
  const fetchData = async () => {
    setLoading(true);
    try {
      // Usar sucursales desde el contexto (cargadas por el Sidebar)
      if (hasValidData) {
        //console.log('📦 [RepuestosMantenedor] Usando sucursales desde contexto');
        setSucursales(sucursalesFromContext);
      } else {
        console.warn('⚠️ [RepuestosMantenedor] No hay sucursales válidas en contexto');
        setSucursales([]);
      }
      
      // Preparar filtros para el backend
      const backendFilters = {};
      
      // Si se quiere incluir inactivos, enviar activo como '' para obtener todos
      // Si no se quiere incluir inactivos, no enviar el parámetro (comportamiento por defecto)
      let repuestosData = [];
      if (selectedSucursal) {
        //console.log("Calling getRepuestos with sucursal:", selectedSucursal);
        
        // Obtener todos los repuestos (activos e inactivos) para filtrado frontend
        const backendFilters = { activo: '' }; // Traer todos
        repuestosData = await repuestosApiService.getRepuestos(selectedSucursal, backendFilters);
      } else {
        repuestosData = await repuestosApiService.getRepuestos(null, {});
      }
      setRepuestos(repuestosData);
    } catch (error) {
      console.error("Error al cargar datos:", error);
      toastService.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };


  // Aplicar filtros a la lista de repuestos
  const applyFilters = () => {
    let filtered = [...repuestos];
    
    // Filtrar por categoría (por ID o por nombre para compatibilidad)
    if (filters.categoria) {
      filtered = filtered.filter(r => {
        // Buscar por categoria_id si existe
        if (r.categoria_id && r.categoria_id.toString() === filters.categoria.toString()) {
          return true;
        }
        // Buscar por nombre de categoría para compatibilidad con datos existentes
        if (r.Categoria === filters.categoria) {
          return true;
        }
        return false;
      });
    }
    
    // Filtrar por stock bajo
    if (filters.stockBajo) {
      filtered = filtered.filter(r => {
        const stock = r.Stock || r.StockSucursal || 0;
        const stockMinimo = r.StockMinimo || r.StockMinimoSucursal || 0;
        return stock <= stockMinimo;
      });
    }
    
    // Filtrar por término de búsqueda
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        r.Nombre.toLowerCase().includes(term) || 
        (r.Descripcion && r.Descripcion.toLowerCase().includes(term))
      );
    }
    
    // Filtrar por estado activo/inactivo (filtrado frontend instantáneo)
    if (!filters.mostrarInactivos) {
      filtered = filtered.filter(r => r.Disponible === true);
    }
    
    setFilteredRepuestos(filtered);
  };

  // Manejar cambios en los filtros
  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Ya no se actualiza selectedSucursal aquí, solo los otros filtros
  };


  // Abrir modal para agregar o editar repuesto
  const handleOpenModal = (repuesto = null) => {
    setSelectedRepuesto(repuesto);
    setIsModalOpen(true);
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRepuesto(null);
  };

  // Abrir modal para ajustar stock
  const handleOpenStockModal = (repuesto) => {
    setSelectedRepuesto(repuesto);
    setIsStockModalOpen(true);
  };

  // Cerrar modal de stock
  const handleCloseStockModal = () => {
    setIsStockModalOpen(false);
    setSelectedRepuesto(null);
  };

  // Manejar envío del formulario
  const handleSubmitForm = async (formData) => {
    try {
      if (selectedRepuesto) {
        // Actualizar repuesto existente
        await repuestosApiService.updateRepuesto(selectedRepuesto.id, formData);
        toastService.success("Repuesto actualizado correctamente");
      } else {
        // Crear nuevo repuesto
        await repuestosApiService.createRepuesto(formData);
        toastService.success("Repuesto creado correctamente");
      }
      
      // Recargar la lista de repuestos
      fetchData();
      
      // Cerrar el modal
      handleCloseModal();
    } catch (error) {
      console.error("Error al guardar repuesto:", error);
      toastService.error("Error al guardar el repuesto");
    }
  };

  // Manejar ajuste de stock
  const handleAdjustStock = async (stockData) => {
    try {
      if (!selectedSucursal) {
        toastService.error("Debe seleccionar una sucursal para ajustar el stock");
        return;
      }
      
      const cantidad = stockData.tipo === 'entrada' ? stockData.cantidad : -stockData.cantidad;
      await repuestosApiService.updateStockRepuesto(selectedRepuesto.id, { operacion: 'set', cantidad, motivo: stockData.motivo });
      toastService.success("Stock actualizado correctamente");
      
      // Recargar la lista de repuestos
      fetchData();
      
      // Cerrar el modal
      handleCloseStockModal();
    } catch (error) {
      console.error("Error al ajustar stock:", error);
      toastService.error("Error al ajustar el stock");
    }
  };

  // Manejar eliminación de repuesto
  const handleDeleteRepuesto = async (id) => {
    try {
      await repuestosApiService.deleteRepuesto(id);
      // Recargar la lista de repuestos
      fetchData();
      toastService.success("Repuesto eliminado correctamente");
    } catch (error) {
      console.error("Error al eliminar repuesto:", error);
      toastService.error("Error al eliminar el repuesto");
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Inventario de Repuestos</h1>
        
        <Button
          variant="primary"
          onClick={() => handleOpenModal()}
          icon={<FaPlus />}
        >
          Agregar Repuesto
        </Button>
      </div>
      
      {/* Filtros con el componente FilterBar mejorado */}
      <FilterBar 
        onSearch={(value) => setFilters({...filters, searchTerm: value})}
        searchValue={filters.searchTerm}
        onReset={() => setFilters({categoria: '', sucursal: '', stockBajo: false, searchTerm: '', mostrarInactivos: false})}
        searchPlaceholder="Buscar repuestos..."
        checkboxFilters={
          <>
            <CheckboxFiltersContainer>
              <CustomCheckbox
                checked={filters.stockBajo}
                onChange={(checked) => {
                  setFilters(prev => ({
                    ...prev,
                    stockBajo: checked
                  }));
                }}
                label="Solo stock bajo"
                id="stockBajo"
                name="stockBajo"
              />
            </CheckboxFiltersContainer>
            
            <CheckboxFiltersContainer>
              <CustomCheckbox
                checked={filters.mostrarInactivos}
                onChange={(checked) => {
                  setFilters(prev => ({
                    ...prev,
                    mostrarInactivos: checked
                  }));
                }}
                label="Incluir inactivos"
                id="mostrarInactivos"
                name="mostrarInactivos"
              />
            </CheckboxFiltersContainer>
          </>
        }
      >
        {/* Filtro por categoría - CustomSelect para consistencia en altura */}
        <CustomSelect
          name="categoria"
          value={filters.categoria}
          onChange={handleFilterChange}
          className="w-full md:min-w-[200px] md:w-auto bg-gray-700 border border-gray-600 rounded text-white"
          placeholder="Todas las categorías"
          options={categorias.map(cat => ({
            id: cat.id,
            value: cat.id,
            label: cat.nombre
          }))}
        />
      </FilterBar>

      {/* Resumen de inventario */}
      <ResumenInventario repuestos={filteredRepuestos} />

      {/* Mostrar cargando o lista de repuestos */}
      {loading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <RepuestosList
          repuestos={filteredRepuestos}
          sucursales={sucursales}
          categorias={categorias}
          onEdit={handleOpenModal}
          onAdjustStock={handleOpenStockModal}
          onDelete={handleDeleteRepuesto}
        />
      )}

      {/* Modal para agregar o editar repuesto */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedRepuesto ? "Editar Repuesto" : "Agregar Repuesto"}
        size="2xl"
        headerExtra={selectedSucursal && (
          <div className="bg-blue-900/30 px-3 py-2 rounded-lg border border-blue-800/50">
            <p className="text-blue-200 text-sm flex items-center">
              <FaMapMarkerAlt className="text-blue-400 mr-2 text-sm" />
              <strong>{sucursalesFromContext.find(s => s.id === selectedSucursal)?.Nombre || 'Desconocida'}</strong>
            </p>
          </div>
        )}
      >
        <RepuestoForm
          initialData={selectedRepuesto || {}}
          sucursales={sucursalesFromContext}
          categorias={categorias}
          onSubmit={handleSubmitForm}
          onCancel={handleCloseModal}
          onStatusChange={fetchData}
        />
      </Modal>
      
      {/* Modal para ajustar stock */}
      <Modal
        isOpen={isStockModalOpen}
        onClose={handleCloseStockModal}
        title="Ajustar Stock"
        size="sm"
      >
        <StockAdjustForm
          repuesto={selectedRepuesto}
          onSubmit={handleAdjustStock}
          onCancel={handleCloseStockModal}
        />
      </Modal>
    </div>
  );
};

/**
 * Componente para ajustar stock de un repuesto
 */
const StockAdjustForm = ({ repuesto, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    tipo: 'entrada',
    cantidad: 1,
    motivo: ''
  });
  
  if (!repuesto) return null;
  
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value ? Number(value) : 0) : value
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (formData.cantidad <= 0) {
      alert("La cantidad debe ser mayor a cero");
      return;
    }
    
    if (!formData.motivo) {
      alert("Por favor, ingresa un motivo para el ajuste");
      return;
    }
    
    onSubmit({
      ...formData,
      repuestoId: repuesto.id
    });
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-4">
        <h3 className="font-medium">{repuesto.Nombre}</h3>
        <div className="text-sm text-gray-400 mb-3">Stock actual: {repuesto.Stock || repuesto.StockSucursal || 0}</div>
        
        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Tipo de ajuste</label>
          <div className="flex gap-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="tipo"
                value="entrada"
                checked={formData.tipo === 'entrada'}
                onChange={handleChange}
                className="mr-2"
              />
              <span>Entrada</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="tipo"
                value="salida"
                checked={formData.tipo === 'salida'}
                onChange={handleChange}
                className="mr-2"
              />
              <span>Salida</span>
            </label>
          </div>
        </div>
        
        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Cantidad</label>
          <input
            type="number"
            name="cantidad"
            value={formData.cantidad}
            onChange={handleChange}
            min="1"
            className="w-full p-2 border rounded bg-gray-700 text-white"
            required
          />
        </div>
        
        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Motivo</label>
          <select
            name="motivo"
            value={formData.motivo}
            onChange={handleChange}
            className="w-full p-2 border rounded bg-gray-700 text-white"
            required
          >
            <option value="">Seleccionar motivo</option>
            {formData.tipo === 'entrada' ? (
              <>
                <option value="compra">Compra a proveedor</option>
                <option value="devolucion">Devolución de cliente</option>
                <option value="ajuste">Ajuste de inventario</option>
                <option value="traslado">Traslado desde otra sucursal</option>
              </>
            ) : (
              <>
                <option value="uso">Usado en servicio</option>
                <option value="venta">Venta directa</option>
                <option value="defecto">Pieza defectuosa</option>
                <option value="traslado">Traslado a otra sucursal</option>
                <option value="ajuste">Ajuste de inventario</option>
              </>
            )}
          </select>
        </div>
      </div>
      
      <div className="flex justify-end space-x-2">
        <Button variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button 
          variant="primary" 
          type="submit"
        >
          Guardar Ajuste
        </Button>
      </div>
    </form>
  );
};

export default RepuestosMantenedor;